import moment from 'moment'
import type { UserData, Nutrient, ActivityLevel, Goal } from '../types'

// ---- Activity & goal policy ----------------------------------------------

export const ACTIVITY_MULTIPLIERS: Record<ActivityLevel, number> = {
  sedentary: 1.2,
  light: 1.375,
  moderate: 1.55,
  active: 1.725,
  very_active: 1.9,
}

export const ACTIVITY_LABELS: Record<ActivityLevel, string> = {
  sedentary: 'Sedentary (little/no exercise)',
  light: 'Light (1–3 days/week)',
  moderate: 'Moderate (3–5 days/week)',
  active: 'Active (6–7 days/week)',
  very_active: 'Very active (hard daily/physical job)',
}

export const GOAL_LABELS: Record<Goal, string> = {
  lose: 'Lose weight',
  maintain: 'Maintain',
  gain: 'Gain weight',
}

const GOAL_KCAL_ADJUST: Record<Goal, number> = { lose: -500, maintain: 0, gain: 350 }
const PROTEIN_G_PER_KG: Record<Goal, number> = { lose: 1.8, maintain: 1.6, gain: 1.8 }

// ---- Basic formulas ------------------------------------------------------

export function ageFromDob(dob: string): number {
  return moment().diff(moment(dob, 'YYYY-MM-DD'), 'years')
}

export function bmi(weightKg: number, heightCm: number): number | null {
  if (!(weightKg > 0) || !(heightCm > 0)) return null
  const m = heightCm / 100
  return weightKg / (m * m)
}

/** Mifflin–St Jeor BMR (kcal/day). */
export function bmrMifflin(weightKg: number, heightCm: number, age: number, sex: 'male' | 'female'): number {
  const base = 10 * weightKg + 6.25 * heightCm - 5 * age
  return sex === 'male' ? base + 5 : base - 161
}

// ---- Reference values (AU/NZ NRVs, NHMRC) --------------------------------
// Adult aim-for targets by sex with a couple of age bands where it matters.

function aimForRdi(sex: 'male' | 'female', age: number): Partial<Record<Nutrient, number>> {
  const male = sex === 'male'
  return {
    fibre: male ? 30 : 25, // AI
    omega3: male ? 1.3 : 0.8, // ALA, g (AI)
    potassium: male ? 3800 : 2800, // mg (AI)
    calcium: age >= 70 ? 1300 : 1000, // mg
    iron: male ? 8 : age >= 51 ? 8 : 18, // mg
    magnesium: male ? 400 : 310, // mg
    zinc: male ? 14 : 8, // mg
    vitA: male ? 900 : 700, // µg RE
    vitC: 45, // mg
    vitD: age >= 70 ? 15 : age >= 51 ? 10 : 5, // µg (AI)
    b12: 2.4, // µg
    folate: 400, // µg DFE
  }
}

// ---- Targets -------------------------------------------------------------

export type TargetKind = 'macro' | 'aim' | 'limit' | 'none'

export interface TargetInfo {
  value: number
  kind: TargetKind
}

export interface Targets {
  age: number
  bmi: number | null
  bmr: number
  tdee: number
  calorieTarget: number
  byNutrient: Partial<Record<Nutrient, TargetInfo>>
}

/** True when enough profile data exists to compute targets. */
export function profileComplete(d: UserData): boolean {
  return (
    d.weight > 0 &&
    !!d.heightCm &&
    d.heightCm > 0 &&
    !!d.dob &&
    (d.gender === 'male' || d.gender === 'female') &&
    !!d.activityLevel &&
    !!d.goal
  )
}

/** Compute all per-nutrient targets from the profile, or null if incomplete. */
export function computeTargets(d: UserData): Targets | null {
  if (!profileComplete(d)) return null
  const sex = d.gender as 'male' | 'female'
  const age = ageFromDob(d.dob!)
  const bmr = bmrMifflin(d.weight, d.heightCm!, age, sex)
  const tdee = bmr * ACTIVITY_MULTIPLIERS[d.activityLevel!]
  const calorieTarget = Math.round(
    d.customCalories && d.customCalories > 0 ? d.customCalories : tdee + GOAL_KCAL_ADJUST[d.goal!],
  )

  const proteinG = Math.round(
    d.customProtein && d.customProtein > 0 ? d.customProtein : PROTEIN_G_PER_KG[d.goal!] * d.weight,
  )
  const fatG = Math.round((0.3 * calorieTarget) / 9)
  const carbsG = Math.max(0, Math.round((calorieTarget - proteinG * 4 - fatG * 9) / 4))

  const aim = aimForRdi(sex, age)

  const byNutrient: Partial<Record<Nutrient, TargetInfo>> = {
    calories: { value: calorieTarget, kind: 'macro' },
    protein: { value: proteinG, kind: 'aim' },
    carbs: { value: carbsG, kind: 'macro' },
    fat: { value: fatG, kind: 'macro' },
    // limits derived from energy
    saturatedFat: { value: Math.round((0.1 * calorieTarget) / 9), kind: 'limit' },
    addedSugar: { value: Math.round((0.1 * calorieTarget) / 4), kind: 'limit' },
    transFat: { value: Math.max(1, Math.round((0.01 * calorieTarget) / 9)), kind: 'limit' },
    // fixed limits
    sodium: { value: 2000, kind: 'limit' },
    cholesterol: { value: 300, kind: 'limit' },
    alcohol: { value: 20, kind: 'limit' },
    caffeine: { value: 400, kind: 'limit' },
  }
  for (const [k, v] of Object.entries(aim)) {
    byNutrient[k as Nutrient] = { value: Math.round(v as number), kind: 'aim' }
  }

  return { age, bmi: bmi(d.weight, d.heightCm!), bmr: Math.round(bmr), tdee: Math.round(tdee), calorieTarget, byNutrient }
}
